import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { ExternalLink, Upload, Search, X, MoreVertical } from 'lucide-react';
import {
  AlertDialog, TextField, TextFieldSlot, IconButton, Tooltip,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  UploadValidationBanner, AssetGrid,
} from '@printtat/ui';
import type { AssetItem } from '@printtat/ui';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBuilderApi } from '../../contexts/BuilderApiContext';
import { BUILDER_QUERY_KEYS } from '../../lib/queryKeys';
import { useBuilderState } from '../../hooks/useBuilderState';
import { useBuilderStore } from '../../store/builderStore';
import { createImageNode, createSvgNode } from '../../utils/nodeUtils';
import { fetchSvgTextAndColors } from '../../utils/svgUtils';
import { runValidation, hasBlockers, getIssues } from '../../lib/validation';
import type { ValidationResult } from '../../lib/validation/types';
import type { DesignNode } from '../../types/node.types';
import type { BuilderAsset, PaginatedResult } from '../../types/api.types';

interface PendingUpload {
  id: string;
  localUrl: string;
  fileName: string;
  status: 'validating' | 'uploading' | 'done' | 'error';
}

/** Map UI filter tab key to API query params */
const parseFilter = (activeFilter: string): { source?: 'user' | 'printtat'; category?: string } => {
  if (activeFilter === 'user') return { source: 'user' };
  if (activeFilter === 'printtat') return { source: 'printtat' };
  if (activeFilter === 'printtat:uncategorized') return { source: 'printtat', category: 'uncategorized' };
  if (activeFilter.startsWith('cat:')) return { source: 'printtat', category: activeFilter.slice(4) };
  return {};
};

/** Simple debounce hook — inlined to avoid external dependency */
const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const REPLACE_ON_CLICK_KEY = 'builder:replaceOnClick';

interface DesignLibraryProps {
  /** URL to "Manage Library" page. If undefined, the link is hidden. */
  manageLibraryUrl?: string;
}

export const DesignLibrary = ({ manageLibraryUrl }: DesignLibraryProps) => {
  const api = useBuilderApi();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addNode, clearCurrentSurface, currentSurface, currentProduct, surfaces, productConfig } = useBuilderState();

  const [replaceOnClick, setReplaceOnClick] = useState(() => {
    const stored = localStorage.getItem(REPLACE_ON_CLICK_KEY);
    return stored === null ? true : stored === 'true';
  });
  const currentLayerCount = (surfaces[currentSurface] || []).length;
  const hasMultipleLayers = currentLayerCount > 1;

  useEffect(() => {
    if (hasMultipleLayers && replaceOnClick) {
      setReplaceOnClick(false);
      localStorage.setItem(REPLACE_ON_CLICK_KEY, 'false');
    }
  }, [hasMultipleLayers, replaceOnClick]);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [activeFilter, setActiveFilter] = useState('all');

  const { source, category } = parseFilter(activeFilter);
  const search = debouncedSearch || undefined;
  const filters = { source, category, search };

  // ── Asset fetching via API adapter ──

  const {
    data: assetData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResult<BuilderAsset>>({
    queryKey: BUILDER_QUERY_KEYS.assets.list(filters as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      api.listAssets({ ...filters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    staleTime: 30_000,
  });

  const assets = useMemo(
    () => assetData?.pages.flatMap((p) => p.data) ?? [],
    [assetData],
  );

  type AssetPages = { pages: PaginatedResult<BuilderAsset>[]; pageParams: number[] };
  const listKey = BUILDER_QUERY_KEYS.assets.list(filters as Record<string, unknown>);

  const invalidateAssets = () =>
    queryClient.invalidateQueries({ queryKey: BUILDER_QUERY_KEYS.assets.all() });

  const addAsset = (asset: BuilderAsset) => {
    queryClient.setQueryData<AssetPages>(listKey, (old: AssetPages | undefined) => {
      if (!old) return old;
      const pages = [...old.pages];
      pages[0] = { ...pages[0], data: [asset, ...pages[0].data], meta: { ...pages[0].meta, total: pages[0].meta.total + 1 } };
      return { ...old, pages };
    });
    invalidateAssets();
  };

  const removeAsset = (id: string) => {
    queryClient.setQueryData<AssetPages>(listKey, (old: AssetPages | undefined) => {
      if (!old) return old;
      const pages = old.pages.map((p: PaginatedResult<BuilderAsset>) => ({
        ...p,
        data: p.data.filter((a: BuilderAsset) => a.id !== id),
        meta: { ...p.meta, total: p.meta.total - 1 },
      }));
      return { ...old, pages };
    });
    invalidateAssets();
  };

  const updateAssetInCache = (id: string, updates: Partial<BuilderAsset>) => {
    queryClient.setQueryData<AssetPages>(listKey, (old: AssetPages | undefined) => {
      if (!old) return old;
      const pages = old.pages.map((p: PaginatedResult<BuilderAsset>) => ({
        ...p,
        data: p.data.map((a: BuilderAsset) => (a.id === id ? { ...a, ...updates } : a)),
      }));
      return { ...old, pages };
    });
    invalidateAssets();
  };

  // ── Asset categories ──

  const { data: categories = [] } = useQuery({
    queryKey: BUILDER_QUERY_KEYS.assetCategories.all(),
    queryFn: () => api.listAssetCategories(),
    staleTime: 5 * 60_000,
  });

  // ── Image upload ──

  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [errors, setErrors] = useState<ValidationResult[]>([]);
  const [warnings, setWarnings] = useState<ValidationResult[]>([]);
  const nextId = useRef(0);

  const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });

  const uploadFile = async (file: File) => {
    const localId = `pending-${nextId.current++}`;
    const localUrl = URL.createObjectURL(file);
    setPending((prev: PendingUpload[]) => [...prev, { id: localId, localUrl, fileName: file.name, status: 'validating' }]);

    try {
      const img = await loadImage(file);
      const results = await runValidation('upload', { file, imageElement: img });
      const blockers = results.filter(r => r.blocker);
      const nonBlockers = getIssues(results).filter(r => !r.blocker);

      if (hasBlockers(results)) {
        setPending(prev => prev.filter(p => p.id !== localId));
        setErrors(blockers);
        setWarnings(nonBlockers);
        URL.revokeObjectURL(localUrl);
        return;
      }

      setPending(prev => prev.map(p => p.id === localId ? { ...p, status: 'uploading' as const } : p));

      const asset = await api.uploadAsset(file, {
        width: img.naturalWidth,
        height: img.naturalHeight,
      });

      setPending(prev => prev.filter(p => p.id !== localId));
      URL.revokeObjectURL(localUrl);
      setWarnings(nonBlockers);

      addAsset(asset);
      if (replaceOnClick) clearCurrentSurface();
      addToCanvas(asset.url, asset.width ?? 200, asset.height ?? 200);
    } catch (err) {
      setPending(prev => prev.filter(p => p.id !== localId));
      URL.revokeObjectURL(localUrl);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setErrors([{ key: 'server', status: 'error', blocker: true, title: 'Upload failed', message: msg }]);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    Array.from(files).forEach(f => { if (f.type.startsWith('image/')) uploadFile(f); });
    event.target.value = '';
  };

  const dismissErrors = () => setErrors([]);
  const dismissWarnings = () => setWarnings([]);

  // ── Usage warning state ──

  const [usageWarning, setUsageWarning] = useState<{ assetId: string; usedBy: Array<{ id: string; name: string }> } | null>(null);

  const overflowCategories = useMemo(() => [
    ...categories.map((c: { slug: string; name: string }) => ({ key: `cat:${c.slug}`, label: c.name })),
    ...(categories.length > 0 ? [{ key: 'printtat:uncategorized', label: 'Uncategorized' }] : []),
  ], [categories]);

  const activeOverflowLabel = useMemo(() => {
    const match = overflowCategories.find(c => c.key === activeFilter);
    return match ? match.label : null;
  }, [activeFilter, overflowCategories]);

  const addToCanvas = async (src: string, width: number, height: number, mimeType?: string) => {
    const dims = productConfig.dimensions[currentSurface];
    const rawW = width || 200;
    const rawH = height || 200;
    const maxW = (dims?.width ?? 440) * 0.6;
    const maxH = (dims?.height ?? 600) * 0.6;
    const fitScale = Math.min(maxW / rawW, maxH / rawH, 1);
    const fittedW = rawW * fitScale;
    const fittedH = rawH * fitScale;

    const freshSurfaces = useBuilderStore.getState().surfaces;
    const currentLayers = freshSurfaces[currentSurface] || [];
    const maxZIndex = currentLayers.length > 0 ? Math.max(...currentLayers.map((n: DesignNode) => n.zIndex)) : 0;
    const position = { x: ((dims?.width ?? 440) - fittedW) / 2, y: ((dims?.height ?? 600) - fittedH) / 2 };
    const size = { width: fittedW, height: fittedH };

    if (mimeType === 'image/svg+xml') {
      const { originalColors } = await fetchSvgTextAndColors(src);
      const node = createSvgNode(src, currentSurface, currentProduct, position, size, originalColors, maxZIndex + 1);
      addNode(node);
    } else {
      const node = createImageNode(src, currentSurface, currentProduct, position, size, maxZIndex + 1);
      addNode(node);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await api.deleteAsset(id);
      if (result.conflict && result.usedBy?.length) {
        setUsageWarning({ assetId: id, usedBy: result.usedBy });
        return;
      }
      removeAsset(id);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleForceDelete = async () => {
    if (!usageWarning) return;
    const { assetId } = usageWarning;
    setUsageWarning(null);
    try {
      await api.deleteAsset(assetId, true);
      removeAsset(assetId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleRename = async (id: string, newName: string) => {
    try {
      const updated = await api.renameAsset(id, newName);
      updateAssetInCache(id, { name: updated.name });
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleToggleReplace = (checked: boolean) => {
    if (checked && hasMultipleLayers) return;
    setReplaceOnClick(checked);
    localStorage.setItem(REPLACE_ON_CLICK_KEY, String(checked));
  };

  const handleAssetClick = (item: AssetItem) => {
    const asset = assets.find(a => a.id === item.id);
    if (!asset) return;
    if (replaceOnClick && !hasMultipleLayers) clearCurrentSurface();
    addToCanvas(asset.url, asset.width ?? 200, asset.height ?? 200, asset.mimeType);
  };

  const handleDragStart = (e: React.DragEvent, item: AssetItem) => {
    const asset = assets.find(a => a.id === item.id);
    if (!asset) return;
    e.dataTransfer.setData('text/plain', JSON.stringify({ src: asset.url, width: asset.width, height: asset.height, mimeType: asset.mimeType }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const assetItems: AssetItem[] = assets.map((a: BuilderAsset) => ({
    id: a.id,
    name: a.name,
    src: a.url,
    width: a.width ?? undefined,
    height: a.height ?? undefined,
    mimeType: a.mimeType,
    hasWarning: a.hasTransparency === false,
    warningText: a.hasTransparency === false ? 'No transparent background detected. The image may cover the garment behind it.' : undefined,
    source: a.source,
  }));

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-1 overflow-y-auto px-2.5 py-2">
        {/* Header with title and upload */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs text-gray-500 py-1">Library</h3>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
          >
            <Upload className="w-2.5 h-2.5" />
            Upload
          </button>
        </div>

        <UploadValidationBanner
          errors={errors}
          warnings={warnings}
          onDismissErrors={dismissErrors}
          onDismissWarnings={dismissWarnings}
        />

        {/* Replace on click toggle */}
        <Tooltip
          content={
            hasMultipleLayers
              ? 'Only available with a single layer. Remove extra layers to re-enable.'
              : 'When enabled, clicking an asset replaces the current design instead of adding a new layer.'
          }
          side="right"
        >
          <label className={`flex items-center gap-1.5 mb-2 select-none ${hasMultipleLayers ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <button
              type="button"
              role="switch"
              aria-checked={replaceOnClick && !hasMultipleLayers}
              disabled={hasMultipleLayers}
              onClick={() => handleToggleReplace(!replaceOnClick)}
              className={`relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors ${
                replaceOnClick && !hasMultipleLayers ? 'bg-blue-600' : 'bg-gray-300'
              } ${hasMultipleLayers ? '' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform ${
                  replaceOnClick && !hasMultipleLayers ? 'translate-x-3' : 'translate-x-0.5'
                }`}
              />
            </button>
            <span className="text-[10px] text-gray-500">Replace design on click</span>
          </label>
        </Tooltip>

        {/* Search */}
        <div className="mb-2">
          <TextField
            size="1"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          >
            <TextFieldSlot>
              <Search className="w-3 h-3 text-gray-400" />
            </TextFieldSlot>
            {searchInput && (
              <TextFieldSlot>
                <IconButton size="xs" onClick={() => setSearchInput('')}>
                  <X className="w-2.5 h-2.5" />
                </IconButton>
              </TextFieldSlot>
            )}
          </TextField>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 mb-1 justify-between">
          {[
            { key: 'all', label: 'All' },
            { key: 'user', label: 'Uploads' },
            { key: 'printtat', label: 'Printtat' },
          ].map(tab => {
            const isActive = activeFilter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`inline-flex items-center px-2 py-1 text-[10px] font-medium rounded-md transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-colors cursor-pointer mr-0.5 ${
                  activeOverflowLabel
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={4}>
              {overflowCategories.map(cat => (
                <DropdownMenuItem
                  key={cat.key}
                  onClick={() => setActiveFilter(cat.key)}
                  className={`text-xs cursor-pointer ${activeFilter === cat.key ? 'font-semibold' : ''}`}
                >
                  {cat.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {activeOverflowLabel && (
          <div className="flex items-center gap-1 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 rounded-full">
              {activeOverflowLabel}
              <button onClick={() => setActiveFilter('all')} className="hover:text-blue-900 cursor-pointer">
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          </div>
        )}

        {/* Unified grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-square bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : assets.length === 0 && pending.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <Upload className="w-6 h-6 mx-auto mb-1 opacity-40" />
            <p className="text-[10px]">
              {search ? 'No matching assets' :
               activeFilter === 'user' ? 'No uploads yet' :
               activeFilter.startsWith('cat:') || activeFilter.startsWith('printtat') ? 'No assets in this category' :
               'No assets yet'}
            </p>
          </div>
        ) : (
          <AssetGrid
            assets={assetItems}
            columns={2}
            showControls={(item) => item.source === 'user'}
            pendingUploads={activeFilter === 'all' || activeFilter === 'user' ? pending : []}
            onAssetClick={handleAssetClick}
            onDelete={handleDelete}
            onRename={handleRename}
            onDragStart={handleDragStart}
            hasMore={hasNextPage ?? false}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={handleLoadMore}
          />
        )}

        {manageLibraryUrl && (
          <a
            href={manageLibraryUrl}
            className="flex items-center justify-center gap-1 text-[10px] text-blue-500 hover:text-blue-700 py-2 mt-2 transition-colors"
          >
            Manage Library
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Usage warning dialog */}
      <AlertDialog
        open={!!usageWarning}
        onOpenChange={(open) => { if (!open) setUsageWarning(null); }}
        title="Image is used in designs"
        description={
          usageWarning
            ? `This image is used in ${usageWarning.usedBy.length === 1 ? '1 design' : `${usageWarning.usedBy.length} designs`}: ${usageWarning.usedBy.map(d => d.name).join(', ')}. Deleting it will leave broken images in those designs.`
            : ''
        }
        confirmText="Delete anyway"
        variant="destructive"
        onConfirm={handleForceDelete}
        onCancel={() => setUsageWarning(null)}
      />
    </div>
  );
};
